import { useGetChildrenList } from 'common/queries-and-mutations/children';
import { useGetSpousesList } from 'common/queries-and-mutations/spouse';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import TableProperties from 'components/TableWidget/TableProperties';
import {
  CHILDREN_TABLE_REPORT,
  SPOUSE_TABLE_REPORT
} from 'constants/download-table-header';
import { debounce } from 'lodash';
import React, { useCallback, useState } from 'react';
import SpouseTable from './Spouse/SpouseTable';
import ChildrenTable from './Children/ChildrenTable';
import { Loader } from 'components/Loader';
import AddSpouseModal from './Spouse/AddSpouseModal';
import withCreatePortal from 'components/HOC/withCreatePortal';
import AddChildrenModal from './Children/AddChildrenModal';

const EnhancedAddSpouseForm = withCreatePortal(AddSpouseModal);
const EnhancedAddChildrenForm = withCreatePortal(AddChildrenModal);

export default function SpouseChildren() {
  const [activeTab, setActiveTab] = useState('Spouse');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [showAddSpouseModal, setShowAddSpouseModal] = useState<boolean>(false);
  const [showAddChildrenModal, setShowAddChildrenModal] =
    useState<boolean>(false);

  const handleTab = (type: string) => {
    setActiveTab(type);
  };

  const executeSearch = useCallback(
    debounce(() => {
      setIsExecutingSearch(true);
    }, 2000),
    []
  );

  const handleChange = (val: any) => {
    setIsExecutingSearch(false);
    if (val === '') {
      setSearchQuery('');
      executeSearch();
    }
    if (val.trim()) {
      setSearchQuery(val);
      executeSearch();
    }
  };

  const {
    data: spouses,
    isLoading: isSpouseLoading,
    refetch: getSpouse
  } = useGetSpousesList({
    enabled: activeTab === 'Spouse' && isExecutingSearch,
    search: searchQuery
  });

  const {
    data: children,
    isLoading: isChildrenLoading,
    refetch: getChildrens
  } = useGetChildrenList({
    enabled: activeTab === 'Children' ? isExecutingSearch : false,
    search: searchQuery
  });

  const isLoading =
    activeTab === 'Spouse' ? isSpouseLoading : isChildrenLoading;

  return (
    <div>
      {isLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3">
            <div className="col-span-2 col-start-1">
              <Breadcrumb title="Spouse & Children" />
            </div>
          </div>

          <TableProperties
            showTab={false}
            activeTab={activeTab}
            handleTab={handleTab}
            searchQuery={searchQuery}
            handleSearch={val => handleChange(val)}
            exportData={activeTab === 'Spouse' ? spouses : children}
            exportHeader={
              activeTab === 'Spouse'
                ? SPOUSE_TABLE_REPORT
                : CHILDREN_TABLE_REPORT
            }
            fileName={activeTab}
            isLoading={isLoading}
            showSpouseTab={true}
            setShowAddModal={() => {
              if (activeTab === 'Spouse') {
                setShowAddSpouseModal(true);
              } else {
                setShowAddChildrenModal(true);
              }
            }}
          />
          <div className="-mt-[2px] min-h-[40rem] overflow-x-auto bg-white sm:rounded-lg">
            {activeTab === 'Spouse' ? (
              <SpouseTable spouses={spouses} onSuccess={getSpouse} />
            ) : (
              <ChildrenTable childrens={children} onSuccess={getChildrens} />
            )}
          </div>
        </>
      )}

      {showAddSpouseModal && (
        <EnhancedAddSpouseForm
          onClose={() => setShowAddSpouseModal(false)}
          onSuccess={() => {
            getSpouse();
            setShowAddSpouseModal(false);
            handleTab('Spouse');
          }}
        />
      )}
      {showAddChildrenModal && (
        <EnhancedAddChildrenForm
          onClose={() => setShowAddChildrenModal(false)}
          onSuccess={() => {
            setShowAddChildrenModal(false);
            handleTab('Children');
            getChildrens();
          }}
        />
      )}
    </div>
  );
}
